<template>
  <div ref="heroRef" :class="$style.wrap" data-hero-block-articles-v="3.2.2">
    <div ref="swiperRef" :class="[$style.schedules, 'swiper']">
      <div class="swiper-wrapper">
        <div
          v-for="article in articlesResponse?.articles"
          :key="article.sys.id"
          :class="[$style.swiperSlide, 'swiper-slide']"
        >
          <HeroBlockArticle
            :data="article"
            :with-nav="(articlesResponse?.articles?.length || 0) > 1"
            @request-prev="onRequestPrev"
            @request-next="onRequestNext"
            @click="handleCTAEvent"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import swiper from 'swiper'
import { EffectFade, Autoplay } from 'swiper/modules'

import type { Swiper } from 'swiper'
import type {
  TypeArticleSkeleton,
  TypeContentBlockWithoutUnresolvableLinksResponse,
} from '~/types/contentful'

import 'swiper/css'
import 'swiper/css/effect-fade'

const { $client, $visibility, $userAccessCheck } = useNuxtApp()
const { highestUserLevel } = storeToRefs(useUserStore())
const { locale } = useI18n()

const heroRef = ref(null)
const swiperRef = ref<(HTMLDivElement & { swiper: Swiper }) | null>(null)
const { isIntersecting } = useIntersectionObserver(heroRef, {}, true)

defineProps<{
  data: TypeContentBlockWithoutUnresolvableLinksResponse
}>()

// const cookieStore = useCookieStore()
const { data: articlesResponse } = await useAsyncData(
  'heroBlockArticles',
  async () => {
    const response =
      await $client.withoutUnresolvableLinks.getEntries<TypeArticleSkeleton>({
        content_type: 'article',
        limit: 10, // pad to 10 to ensure we get 3
        locale: locale.value,
        include: 10,
        order: ['-fields.publishedDateTime'],
        'fields.showInArticleHeroCarousel': true,
      })

    const items = response?.items ?? []

    const filtered = items.filter((item) => {
      const accessLevel = item.fields?.accessLevel
      const isVisible = $visibility(item.fields?.visibility)
      const hasAccess = $userAccessCheck(accessLevel, highestUserLevel.value)

      return isVisible && hasAccess
    })

    return filtered
  },
  {
    transform(input) {
      return {
        articles: input,
        fetchedAt: useUnixTimestamp(),
      }
    },
    getCachedData: (key) => useCachedData(key),
    // watch: [() => cookieStore.languageCookie, () => cookieStore.countryCookie],
    watch: [() => locale.value],
  },
)

const onRequestPrev = () => {
  swiperRef.value?.swiper.slidePrev()
}
const onRequestNext = () => {
  swiperRef.value?.swiper.slideNext()
}

const handleCTAEvent = () => {
  if (isIntersecting && swiperRef?.value?.swiper) {
    const { realIndex } = swiperRef.value.swiper
    const slide = articlesResponse.value?.articles?.[realIndex]

    pushToGaDataLayer({
      event: 'ga4.trackEvent',
      eventName: `${useRoute().params?.slug?.[0] || 'homepage'}_banner_click`,
      eventParams: {
        item_name: slide?.fields?.productName,
        set_name: slide?.fields?.setName,
        content_position: realIndex + 1,
        content_name: slide?.fields?.entryTitle,
      },
    })
  }
}

const viewEvents = reactive<Record<number, boolean>>({})

function fireViewEvent() {
  if (isIntersecting && swiperRef?.value?.swiper) {
    const { realIndex = 0 } = swiperRef.value?.swiper || {}
    const slide = articlesResponse.value?.articles?.[realIndex || 0]

    if (slide && !viewEvents[realIndex]) {
      // console.log('item_name:', slide?.fields?.productName)
      // console.log('set_name:', slide?.fields?.setName)
      // console.log('content_position:', realIndex + 1)
      // console.log('content_name:', slide?.fields?.entryTitle)

      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        event: 'ga4.trackEvent',
        eventName: `${useRoute().params?.slug?.[0] || 'homepage'}_banner_view`,
        eventParams: {
          item_name: slide?.fields?.productName,
          set_name: slide?.fields?.setName,
          content_position: realIndex + 1,
          content_name: slide?.fields?.entryTitle,
        },
      })

      viewEvents[realIndex] = true
    }
  }
}

onMounted(() => {
  if (swiperRef.value) {
    new swiper(swiperRef.value, {
      modules: [Autoplay, EffectFade],
      autoplay: {
        delay: 8000,
        disableOnInteraction: true,
      },
      loop: true,
      speed: 1000,
      effect: 'fade',
      fadeEffect: {
        crossFade: true,
      },
    })

    // on swipe init, fire view event
    fireViewEvent()

    // on swipe change, fire view event
    swiperRef.value.swiper.on('slideChange', () => {
      fireViewEvent()
    })
  }
})
</script>

<style lang="scss" module>
.wrap {
  background: var(--black);
}

.swiperSlide {
  height: auto;
}

:global {
  .fade-left-enter-active {
    transition: transform cubic-bezier(0.16, 1, 0.29, 0.99) 0.667s;
  }

  .fade-left-enter, .fade-left-leave-to /* .fade-leave-active in <2.1.8 */ {
    opacity: 0;
    transform: translateX(40px);
  }

  .fade-right-enter-active {
    transition: transform cubic-bezier(0.16, 1, 0.29, 0.99) 0.667s;
  }

  .fade-right-enter, .fade-right-leave-to /* .fade-leave-active in <2.1.8 */ {
    opacity: 0;
    transform: translateX(-40px);
  }
}

:global {
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.4s;
  }
  .fade-enter, .fade-leave-to /* .fade-leave-active in <2.1.8 */ {
    opacity: 0;
  }
}
</style>
